import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { shipToAddressService, ShipToAddress, ShipToAddressUpdate } from '../../services/shipToAddressService';
import { useTranslation } from 'react-i18next';

interface ShipToAddressesProps {
  customerId?: string;
}

export const ShipToAddresses: React.FC<ShipToAddressesProps> = ({ customerId }) => {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState<ShipToAddress[]>([]);
  const [primaryAddress, setPrimaryAddress] = useState<ShipToAddress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    address: ShipToAddress | null;
  }>({ open: false, address: null });
  const [editForm, setEditForm] = useState<ShipToAddressUpdate>({});
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadShipToAddresses();
  }, []);

  const loadShipToAddresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await shipToAddressService.getShipToAddresses(true);
      setAddresses(response.addresses);
      setPrimaryAddress(response.primary_address || null);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.shipToLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (addressId: string) => {
    try {
      setUpdating(true);
      const updatedAddress = await shipToAddressService.setPrimaryAddress(addressId);
      setPrimaryAddress(updatedAddress);
      
      // Update the addresses list
      setAddresses(prevAddresses => 
        prevAddresses.map(addr => ({
          ...addr,
          is_primary: addr.id === addressId
        }))
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.shipToSetPrimaryFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleEditClick = (address: ShipToAddress) => {
    setEditForm({
      name: address.name,
      contact_name: address.contact_name || '',
      phone: address.phone || '',
      email: address.email || ''
    });
    setEditDialog({ open: true, address });
  };

  const handleEditClose = () => {
    setEditDialog({ open: false, address: null });
    setEditForm({});
  };

  const handleEditSave = async () => {
    if (!editDialog.address) return;

    try {
      setUpdating(true);
      const updatedAddress = await shipToAddressService.updateShipToAddress(
        editDialog.address.id,
        editForm
      );

      // Update the addresses list
      setAddresses(prevAddresses => 
        prevAddresses.map(addr => 
          addr.id === updatedAddress.id ? updatedAddress : addr
        )
      );

      // Update primary address if it's the same one
      if (primaryAddress?.id === updatedAddress.id) {
        setPrimaryAddress(updatedAddress);
      }

      handleEditClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.shipToUpdateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (addresses.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('profile.shipToAddresses')}
          </Typography>
          <Typography color="text.secondary">
            No ship-to addresses found. Ship-to addresses will be synced from Epicor during the next customer sync.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationIcon color="primary" />
          Ship-to Addresses ({addresses.length})
        </Typography>

        <List>
          {addresses.map((address, index) => (
            <React.Fragment key={address.id}>
              <ListItem alignItems="flex-start">
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  {/* Address Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {address.name}
                    </Typography>
                    {address.is_primary && (
                      <Chip
                        icon={<StarIcon />}
                        label={t('cart.primary')}
                        size="small"
                        color="primary"
                        variant="filled"
                      />
                    )}
                    <Chip
                      label={`#${address.epicor_ship_to_num}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {/* Address Details */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {address.full_address}
                  </Typography>

                  {/* Contact Information */}
                  {(address.contact_name || address.phone || address.email) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                      {address.contact_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {address.contact_name}
                          </Typography>
                        </Box>
                      )}
                      {address.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {address.phone}
                          </Typography>
                        </Box>
                      )}
                      {address.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {address.email}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(address)}
                      disabled={updating}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {!address.is_primary && (
                      <IconButton
                        size="small"
                        onClick={() => handleSetPrimary(address.id)}
                        disabled={updating}
                        title={t('profile.setAsPrimary')}
                      >
                        <StarBorderIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </ListItem>
              
              {index < addresses.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        {/* Note about sync */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Ship-to addresses are managed in Epicor ERP and synced to this portal. 
          You can update contact information but not the physical addresses.
        </Typography>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('profile.editContactInfo')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('profile.addressName')}
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('profile.contactPerson')}
              value={editForm.contact_name || ''}
              onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('profile.phoneNumber')}
              value={editForm.phone || ''}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('profile.emailAddress')}
              type="email"
              value={editForm.email || ''}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEditSave}
            disabled={updating}
          >
            {updating ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};